import {
  ExtendedWarrantyPremiumRightRideDigital,
  IAPrograms,
} from "@modules/purchase/types/IAProgram";
import { Trans, useTranslation } from "next-i18next";
import {
  Purchase,
  PurchasePreview,
  PurchasePricing,
  PurchaseProductConfiguration,
} from "@modules/purchase/types/Purchase";
import PurchaseInformationListItem from "@modules/purchase/components/aftermarket/PurchaseInformationListItem";
import { useRouter } from "next/router";
import { formatCurrency } from "@common/helpers/numberFormatting";
import classNames from "classnames";
import getDisplayCoverageLength from "@modules/purchase/helpers/getDisplayCoverageLength";
import PurchaseSourceVehicleImage from "@modules/purchase/components/PurchaseSourceVehicleImage";
import Button from "@common/components/Button";
import { useConsumerPurchaseLayoutContext } from "@modules/purchase/ConsumerPurchaseLayoutContext";
import { useMemo, useState } from "react";
import {
  aftermarketCompleteStep,
  aftermarketContractStep,
  aftermarketCoverageStep,
  aftermarketDetailsStep,
  aftermarketPaymentStep,
} from "@modules/purchase/types/PurchaseStep";
import { TranslationKeys } from "src/@types/i18next";
import { LocalDate } from "@js-joda/core";
import VehicleAvatar from "@common/components/VehicleAvatar";
import Link from "next/link";
import Badge from "@common/components/Badge";
import { CloseIcon } from "@common/components/icons";
import useAftermarketRemovePromoCodeMutation from "@modules/purchase/hooks/useAftermarketRemovePromoCodeMutation";
import PurchaseIntroduction from "../PurchaseIntroduction";
import ApplyPromoCodeModal from "./ApplyPromoCodeModal";

type Props = {
  purchase: Purchase;
  purchasePreview?: PurchasePreview;
};

const PurchaseInformation = ({ purchase, purchasePreview }: Props) => {
  const { t } = useTranslation(["purchase", "common"]);
  const router = useRouter();
  const locale = router?.locale || "en";
  const { showCoverageDetailsModalState } = useConsumerPurchaseLayoutContext();
  const [, setShowCoverageDetailsModal] = showCoverageDetailsModalState;

  const [isAddPromoModalOpen, setIsAddPromoModalOpen] = useState(false);
  const removePromoCodeMutation = useAftermarketRemovePromoCodeMutation();
  const onRemovePromoCode = () => {
    removePromoCodeMutation.mutate({
      dealId: purchase.id,
      promoCode: purchase.appliedPromoCode?.name || "",
    });
  };

  const getVehicleDetailsList = () => {
    if (purchase && purchase?.vehicle && purchase.vehicle.vin) {
      return (
        <ul
          className={classNames({
            "border-t border-gray-100": !!purchase.vehicle,
          })}
        >
          <PurchaseInformationListItem
            key="vehicle-vin"
            label={t("common:vehicle_vin")}
            value={purchase.vehicle.vin}
          />
        </ul>
      );
    }
    return null;
  };

  const coverageStepsKey = useMemo(() => {
    const sortedAftermarketSteps = [
      aftermarketDetailsStep,
      aftermarketCoverageStep,
      aftermarketContractStep,
      aftermarketPaymentStep,
      aftermarketCompleteStep,
    ].sort((a, b) => a.order - b.order);

    return sortedAftermarketSteps.map((step) => ({
      title: t(
        step?.guideTitle as
          | TranslationKeys["common"]
          | TranslationKeys["purchase"]
      ),
      description: t(
        step?.guideDescription as
          | TranslationKeys["common"]
          | TranslationKeys["purchase"]
      ),
    }));
  }, [t]);

  const getCoveragePlanDetailsList = (config: PurchaseProductConfiguration) => {
    const iAProgram = IAPrograms.find((iap) => iap.id === config.programId);

    if (!iAProgram) return null;

    const programI18nKey = iAProgram.coverageTypes.find(
      (ct) => ct.abbr === config.coverageTypeId
    )?.i18nKey;

    const isPremiumCoverage =
      iAProgram.id === ExtendedWarrantyPremiumRightRideDigital.id;

    const term = getDisplayCoverageLength(
      (config.term as number) ?? 0,
      (config.termKms as number) ?? 0,
      locale,
      isPremiumCoverage ? (config.expiryDate as LocalDate) : undefined
    );

    return (
      <ul
        key="plan-details-list"
        className={purchase?.vehicle ? "border-t border-gray-100" : ""}
      >
        {programI18nKey && (
          <PurchaseInformationListItem
            key="warranty-plan"
            isHighlighted
            label={t("purchase:warranty_plan")}
            value={t(programI18nKey)}
          />
        )}
        <li>
          <div className="-mt-2 -mx-2 flex body-3 justify-end">
            <Button
              fill="link"
              spacing="no-padding"
              className="underline hover:no-underline"
              onClick={() => setShowCoverageDetailsModal(true)}
            >
              {t("purchase:view_coverage_details")}
            </Button>
          </div>
        </li>
        {config.term != null && config.termKms != null && (
          <PurchaseInformationListItem
            key="warranty-term"
            label={t("purchase:coverage_terms_label")}
            value={term}
          />
        )}
        {config.deductible != null && (
          <PurchaseInformationListItem
            key="warranty-deductible"
            label={t("common:deductible")}
            value={formatCurrency(config.deductible as number, locale)}
          />
        )}
      </ul>
    );
  };

  const getCoverageCostDetailsList = (
    config: PurchaseProductConfiguration,
    pricing: PurchasePricing
  ) => {
    const iAProgram = IAPrograms.find((iap) => iap.id === config.programId);

    if (!iAProgram) return null;

    const hasValidTaxDetails = pricing.taxes.some((tb) => tb.amount > 0);

    return (
      <>
        <div className="border-b border-t border-gray-100">
          <ul key="discount">
            <li className="body-3">
              <div className="my-3 flex text-sm sm:body-3 justify-between items-center gap-2">
                {pricing.discount > 0 ? (
                  <>
                    <div className="w-3/4">
                      <h5 className="inline pr-1">
                        {t("purchase:discount_applied")}
                      </h5>
                      {purchase.appliedPromoCode && (
                        <Badge
                          as="button"
                          onRightIconClick={() => onRemovePromoCode()}
                          rightIcon={<CloseIcon />}
                          text={purchase.appliedPromoCode?.name}
                          textStyle="dark"
                          badgeStyle="primary-alt"
                          size="small"
                        />
                      )}
                    </div>
                    <div className="w-1/4 text-right">
                      <h5>{formatCurrency(pricing.discount, locale)}</h5>
                    </div>
                  </>
                ) : (
                  <>
                    <div
                      className="w-3/4 text-primary-bold underline"
                      onClick={() => setIsAddPromoModalOpen(true)}
                      onKeyDown={() => setIsAddPromoModalOpen(true)}
                      role="button"
                      tabIndex={0}
                    >
                      {t("purchase:apply_promo_code").toUpperCase()}
                    </div>
                    <div className="w-1/4 text-right">
                      <h5>{formatCurrency(0, locale)}</h5>
                    </div>
                  </>
                )}
              </div>
            </li>
          </ul>
        </div>

        <ul
          key="cost-details-list"
          className={purchase?.vehicle ? "border-b border-gray-100" : ""}
        >
          {pricing.subTotal > 0 && (
            <PurchaseInformationListItem
              key="subtotal"
              label={t("purchase:subtotal")}
              value={formatCurrency(pricing.subTotal, locale)}
            />
          )}
          {hasValidTaxDetails &&
            pricing.taxes.map((tax) => {
              return (
                <PurchaseInformationListItem
                  key={tax.name}
                  label={tax.name}
                  value={formatCurrency(tax.amount, locale)}
                />
              );
            })}
        </ul>
        {pricing.totalPrice > 0 && (
          <>
            <ul key="total-cost-list">
              <PurchaseInformationListItem
                key="total-cost"
                label={t("common:total_cost")}
                value={formatCurrency(pricing.totalPrice, locale)}
              />
            </ul>
            <div className="caption-1 font-medium">
              <Trans
                t={t}
                i18nKey="purchase:affirm_payment_plans_are_available"
                components={{
                  affirmLink: (
                    <Link
                      className="text-primary-bold underline"
                      href="https://www.affirm.com/en-ca/"
                      target="_blank"
                    />
                  ),
                }}
              />
            </div>
          </>
        )}
      </>
    );
  };

  const getAllCoveragePlanDetailsList = () => {
    if (purchasePreview != null) {
      return getCoveragePlanDetailsList(purchasePreview.product.config);
    }
    return purchase.products.map((p) => {
      return getCoveragePlanDetailsList(p.config);
    });
  };

  const getAllCoverageCostDetailsList = () => {
    if (purchasePreview != null) {
      return getCoverageCostDetailsList(
        purchasePreview.product.config,
        purchasePreview.pricing
      );
    }
    return (
      purchase &&
      purchase.products.map((p) => {
        return getCoverageCostDetailsList(p.config, purchase.pricing);
      })
    );
  };

  const isOnaftermarketDetailsStep =
    router.pathname === aftermarketDetailsStep.href;

  return (
    <>
      <div className="lg:hidden flex flex-col gap-4">
        {isOnaftermarketDetailsStep && (
          <div className="flex flex-col justify-center items-center align-middle py-2 px-1">
            <PurchaseIntroduction
              stepTiles={coverageStepsKey}
              headerText={t("purchase:lets_get_started_exclamation")}
              introText={t(
                "purchase:in_just_a_few_steps_you_will_get_coverage"
              )}
              purchaseType="warranty"
            />
          </div>
        )}
        {!isOnaftermarketDetailsStep && (
          <div className="">
            {getVehicleDetailsList()}
            {getAllCoveragePlanDetailsList()}
            {getAllCoverageCostDetailsList()}
          </div>
        )}
      </div>

      <div className="hidden lg:flex flex-col gap-5 items-left mx-6 2xl:mx-8 mb-12">
        <div className="flex flex-col gap-3 w-full">
          {purchase.vehicle?.image && (
            <div className="pt-12">
              <PurchaseSourceVehicleImage
                className="rounded-xl w-full max-h-56 object-cover"
                src={purchase.vehicle.image}
                alt="Vehicle picture for purchase"
              />
            </div>
          )}

          {isOnaftermarketDetailsStep && (
            <div className="flex flex-col justify-center items-center align-middle px-1 lg:px-2">
              <PurchaseIntroduction
                stepTiles={coverageStepsKey}
                headerText={t("purchase:lets_get_started_exclamation")}
                introText={t(
                  "purchase:in_just_a_few_steps_you_will_get_coverage"
                )}
                purchaseType="warranty"
              />
            </div>
          )}

          {!isOnaftermarketDetailsStep && (
            <>
              <div>
                <h5>{t("purchase:order_summary")}</h5>
              </div>

              <div className="flex gap-3">
                {purchase.vehicle &&
                  purchase.vehicle.vin &&
                  purchase.vehicle.year && (
                    <div>
                      <VehicleAvatar
                        year={purchase.vehicle.year}
                        make={purchase.vehicle.make}
                        bodyStyle={purchase.vehicle.bodyStyle}
                      />
                    </div>
                  )}
                <div className="w-full pt-4">
                  {!!(
                    purchase.vehicle &&
                    purchase.vehicle.vin &&
                    purchase.vehicle.year &&
                    purchase.vehicle.make &&
                    purchase.vehicle.model
                  ) && (
                    <h3>
                      {purchase.vehicle.year} {purchase.vehicle.make}{" "}
                      {purchase.vehicle.model}
                    </h3>
                  )}
                  {purchase.vehicle && purchase.vehicle.vin && (
                    <div className="flex gap-2 mb-3 text-gray-500">
                      {purchase.vehicle.trim && (
                        <h6>{purchase.vehicle.trim}</h6>
                      )}
                      {purchase.vehicle.odometerCount > 0 && (
                        <h6>
                          {purchase.vehicle.trim ? " | " : ""}
                          {purchase.vehicle.odometerCount.toLocaleString()}{" "}
                          {t("common:km")}
                        </h6>
                      )}
                    </div>
                  )}
                </div>
              </div>
              {getVehicleDetailsList()}
              {getAllCoveragePlanDetailsList()}
              {getAllCoverageCostDetailsList()}
            </>
          )}
        </div>
      </div>
      {isAddPromoModalOpen && (
        <ApplyPromoCodeModal
          isOpen={isAddPromoModalOpen}
          dealId={purchase.id}
          onClose={() => setIsAddPromoModalOpen(false)}
        />
      )}
    </>
  );
};

export default PurchaseInformation;
